import { getSessionStore } from "app/utils/sessionStore";
import { FilePreviewInterface, THEME } from "app/utils/types";
import { capitalizeFirstLetter, getFileName } from "app/utils/utils";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sidebar } from "primereact/sidebar";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImFileZip } from "react-icons/im";

const FilePreview: FC<FilePreviewInterface> = ({
  url,
  isHalfWidth = true,
  onCancel,
  loading,
  docCategory,
  formattedDateSubmitted,
  setButtons = <></>,
  content = null,
  downloadFileName = null,
}) => {
  const { t } = useTranslation();
  const globalTheme = getSessionStore("globalTheme");
  const [isLoading, setIsLoading] = useState(loading);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [docCategory]);

  const containsNumbers = (str: any) => /\d/.test(str);

  const downloadFile = () => {
    const sanitizedDownloadName = downloadFileName?.trim();
    let finalFileName =
      sanitizedDownloadName || (url ? getFileName(url) : "") || docCategory?.trim() || "doc";

    // Ensure the filename has an extension so the browser doesn't fall back to "doc"
    if (finalFileName && !/\.[a-z0-9]+$/i.test(finalFileName)) {
      const extensionFromUrl = url?.split("?")[0]?.split(".").pop();
      const cleanedExtension =
        extensionFromUrl && extensionFromUrl.length <= 5 ? extensionFromUrl.toLowerCase() : "pdf";
      finalFileName = `${finalFileName}.${cleanedExtension}`;
    }

    // Trigger download via hidden anchor without opening a new tab
    const triggerAnchorDownload = (href: string) => {
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", finalFileName);
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    // As a last resort for cross-origin URLs in Safari, load via a hidden iframe
    // This avoids opening a new tab while still initiating the download
    const triggerIframeDownload = (href: string) => {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = href;
      document.body.appendChild(iframe);
      // Clean up after some time
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
        } catch {
          // ignore
        }
      }, 60000);
    };

    // If the URL is already a Blob URL, download directly without refetching
    if (url?.startsWith("blob:")) {
      triggerAnchorDownload(url);
      return;
    }

    // Attempt to fetch the file and download as a blob (best UX, keeps same tab)
    // If this fails due to CORS or other issues, fall back to hidden iframe
    try {
      fetch(url!, { method: "GET", mode: "cors", credentials: "omit" })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const objectUrl = window.URL.createObjectURL(blob);
          triggerAnchorDownload(objectUrl);
          // Revoke the object URL shortly after to free memory
          setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
        })
        .catch(() => {
          // Fallback path that avoids opening new tabs
          triggerIframeDownload(url!);
        });
    } catch {
      triggerIframeDownload(url!);
    }
  };

  // Derive a readable title for the header if docCategory is not provided
  const getReadableTitle = (): string => {
    // Prefer explicit docCategory when available
    if (docCategory) {
      if (containsNumbers(docCategory)) {
        return docCategory;
      }
      if (docCategory === "resolutionToAdoptKeyPersonInsurance") {
        return "Resolution to Adopt Key Person";
      }
      const spaced = docCategory
        .split(/(?=[A-Z])/)
        .map((el) => capitalizeFirstLetter(el))
        .join(" ")
        .trim();
      if (spaced) return spaced;
    }

    // Fall back to the provided download file name or url-derived name
    const raw = downloadFileName || "" || (url ? getFileName(url) : "") || "";
    if (!raw) return "";
    const noQuery = raw.split("?")[0];
    const noExt = noQuery.replace(/\.[^/.]+$/, "");
    const cleaned = noExt.replace(/[_-]+/g, " ").trim();
    if (!cleaned) return "";
    // Capitalize each word
    return cleaned
      .split(" ")
      .filter(Boolean)
      .map((w) => capitalizeFirstLetter(w))
      .join(" ");
  };

  const renderContent = () => {
    if (content) {
      return content;
    }

    if (!url) {
      return (
        <p className="w-full h-30rem flex-1 border rounded-md border-gray-500">
          {t("pdfNotGeneratedText")}
        </p>
      );
    }

    if (url.includes(".zip")) {
      return <ImFileZip style={{ height: "50px", width: "50px" }} />;
    }

    return (
      <iframe
        title="PDF Viewer"
        src={`${url}#toolbar=0&navpanes=0`}
        className="w-full h-full flex-1 border rounded-md border-gray-500"
        onLoad={() => {
          setIsLoading(false);
        }}
      />
    );
  };

  return (
    <Sidebar
      visible={!!url || isLoading}
      position="right"
      className={`z-50 ${isHalfWidth ? "!w-1/2 overflow-auto" : "!w-full overflow-auto"} ${
        globalTheme === THEME.DARK ? " " : "bg-white"
      }`}
      onHide={onCancel}
    >
      <div
        className={`flex ${
          globalTheme === THEME.DARK ? " " : "bg-white"
        } flex-col justify-between h-full overflow-auto`}
      >
        <div>
          <div className="p-2 border flex items-end">
            <div className="flex-grow">
              <div className="text-xl">{getReadableTitle()}</div>
            </div>

            <div className="text-right text-md ml-auto">
              <Button
                className="rounded-full !bg-light-gray hover:!bg-gray-700 border-0 !h-7 !w-7 mr-4"
                icon="pi pi-download"
                onClick={() => downloadFile()}
              />
              {formattedDateSubmitted}
            </div>
          </div>

          <Divider type="solid" className="custom-divider" />

          <div
            className={`w-full h-[calc(100vh-${setButtons ? "250px" : "150px"})]`}
            style={{
              height: setButtons ? "calc(100vh - 250px)" : "calc(100vh - 150px)",
            }}
          >
            {isLoading ? (
              <ProgressSpinner
                className={
                  isHalfWidth
                    ? "!w-1/2 items-center flex justify-center"
                    : "!w-full items-center flex justify-center"
                }
              />
            ) : (
              renderContent()
            )}
          </div>
        </div>
        {docCategory !== "resolutionToAdoptKeyPersonInsurance" && setButtons}
      </div>
    </Sidebar>
  );
};

export default FilePreview;
